import {
  CustomViewComponent,
  CustomViewPageCategory,
  CustomViewPages,
  CustomViewTemplateIdType,
  CustomViewTypeIdType,
  useDataPath,
} from '@agilelab/plugin-wb-platform';
import componentPreview from './preview/builder_component/builder_component.png';
import componentThumbnail from './preview/builder_component/builder_component_thumbnail.png';
import entityPreview from './preview/builder_entity_content/builder_entity_content.png';
import entityThumbnail from './preview/builder_entity_content/builder_entity_content_thumbnail.png';
import { ReactElement } from 'react';

/**
 * Generates entity fields based on entity data and type
 * @param data Entity data containing type information and relationships
 * @returns Array of ReactElements representing entity fields
 */
export const generateEntityFields = (data: any): ReactElement[] => {
  // TODO: this needs to be fixed, custom view content should be static and any conditional logic should be implemented using <CustomViewComponent /> attributes such as "showWhen".
  const commonFields: ReactElement[] = [
    <CustomViewComponent type="string" label="URN" path="urn" colSpan="2" />,
    <CustomViewComponent
      type="string"
      label="Description"
      path="entity.metadata.description"
      default="No description"
      colSpan="4"
    />,
  ];

  const systemPrototypeFields: ReactElement[] =
    data?.entity.kind === 'SystemPrototypeType'
      ? [
          <CustomViewComponent
            type="link"
            label="Template"
            path="entity.spec.template"
            colSpan="2"
            showWhenExists="entity.spec.template"
          />,
          <CustomViewComponent
            type="link"
            label="Evolves Into"
            path="entity.spec.evolvesInto"
            colSpan="2"
            showWhenExists="entity.spec.evolvesInto"
          />,
        ]
      : [];

  const nonPracticeShaperFields: ReactElement[] = !data?.isPracticeShaperEntity
    ? [
        <CustomViewComponent
          type="link"
          label="Owner"
          path="_relations.ownedByRelations"
          showWhenExists="_relations.ownedByRelations.length"
          kind="group"
          colSpan="2"
        />,
        <CustomViewComponent
          type="string"
          label="Lifecycle"
          path="entity.spec.lifecycle"
          showWhenHasValue
          colSpan="2"
        />,
        <CustomViewComponent
          type="string"
          label="Type"
          path="entity.spec.type"
          colSpan="2"
          showWhenExists="entity.spec.type"
        />,
      ]
    : [];

  const systemFields: ReactElement[] = data?._types.isSystem
    ? [
        <CustomViewComponent
          type="link"
          label="Domain"
          path="_relations.partOfDomainRelations"
          kind="domain"
          colSpan="2"
        />,
        <CustomViewComponent
          type="string"
          label="Partially Deployable In"
          path="partiallyDeployableIn"
          showWhenHasValue
          colSpan="2"
        />,
      ]
    : [];

  const componentApiResourceFields: ReactElement[] =
    data?._types.isAPI || data?._types.isComponent || data?._types.isResource
      ? [
          <CustomViewComponent
            type="link"
            label="System"
            path="_relations.partOfSystemRelations"
            kind="system"
            colSpan="2"
            default="No System"
          />,
        ]
      : [];

  const componentFields: ReactElement[] = data?._types.isComponent
    ? [
        <CustomViewComponent
          type="link"
          label="Parent Component"
          path="_relations.partOfComponentRelations"
          kind="domain"
          colSpan="2"
          default="No Parent Component"
        />,
      ]
    : [];

  const locationFields: ReactElement[] =
    data?._types.isLocation && data?.targets?.length > 0
      ? [
          <CustomViewComponent
            type="link_grid_list"
            label="Targets"
            path="targets"
          />,
        ]
      : [];

  const taxonomyFields: ReactElement[] =
    data?._types.isSystem || data?._types.isHeadlessComponent
      ? [
          <CustomViewComponent
            type="link"
            label="{{ taxonomyLabel }}"
            path="taxonomies"
            colSpan="2"
          />,
        ]
      : [];

  let practiceShapeFields: ReactElement[] = [];
  if (data?.isPracticeShaperEntity && !!data?.entity.spec) {
    practiceShapeFields.push(
      <CustomViewComponent
        type="automatic_fields_list"
        path="entity.spec"
        exclude={[
          'belongsTo',
          'partOfDomain',
          'partOfSystem',
          'template',
          'evolvesInto',
        ]}
        defaults={{
          _default: { colSpan: '2' },
          description: { colSpan: '4' },
        }}
      />,
    );

    if (data?.entity.kind === 'ComponentType') {
      practiceShapeFields = [
        ...practiceShapeFields,
        <CustomViewComponent
          type="link"
          label="Part of system"
          path="entity.spec.partOfSystem"
          colSpan="2"
          showWhenExists="entity.spec.partOfSystem"
        />,
        <CustomViewComponent
          type="link"
          mode="linear"
          label="Traits"
          path="implementsRelations"
          colSpan="2"
          showWhenExists="implementsRelations"
        />,
      ];
    }

    if (data?.entity.kind === 'SystemType') {
      practiceShapeFields = [
        ...practiceShapeFields,
        <CustomViewComponent
          type="link"
          label="part of domain"
          path="entity.spec.partOfDomain"
          colSpan="2"
          showWhenExists="entity.spec.partOfDomain"
        />,
        <CustomViewComponent
          type="link"
          label="belongs to"
          path="entity.spec.belongsTo"
          colSpan="2"
          showWhenExists="entity.spec.belongsTo"
        />,
        <CustomViewComponent
          type="link"
          mode="linear"
          label="Traits"
          path="implementsRelations"
          colSpan="2"
          showWhenExists="implementsRelations"
        />,
      ];
    }
  }

  const domainTypeFields: ReactElement[] =
    data?.entity.kind === 'DomainType'
      ? [
          <CustomViewComponent
            type="link"
            mode="linear"
            label="Traits"
            path="implementsRelations"
            colSpan="2"
            showWhenExists="implementsRelations"
          />,
        ]
      : [];

  const taxonomyTypeFields: ReactElement[] =
    data?.entity.kind === 'TaxonomyType'
      ? [
          <CustomViewComponent
            type="link"
            mode="linear"
            label="Traits"
            path="implementsRelations"
            colSpan="2"
            showWhenExists="implementsRelations"
          />,
        ]
      : [];

  return [
    ...commonFields,
    ...systemPrototypeFields,
    ...nonPracticeShaperFields,
    ...systemFields,
    ...componentApiResourceFields,
    ...componentFields,
    ...locationFields,
    ...practiceShapeFields,
    ...domainTypeFields,
    ...taxonomyTypeFields,
    ...taxonomyFields,
  ];
};

CustomViewPages.register({
  id: 'builder_entity_content',
  meta: {
    label: 'Builder Entity Content',
    category: CustomViewPageCategory.BUILDER,
    preview: {
      thumbnail: { src: entityThumbnail },
      items: [{ src: entityPreview }],
    },
  },
  function: function Entity() {
    const data = useDataPath('');
    const genFields = generateEntityFields(data);

    return (
      <>
        <CustomViewComponent type="catalog_warnings" />
        <CustomViewComponent type="catalog_system_card">
          <CustomViewComponent type="container">
            {genFields}
            <CustomViewComponent
              type="hline"
              showWhen={{
                value: '{{ tags.length }}',
                notEquals: 0,
              }}
            />
            <CustomViewComponent
              type="tags"
              label="Tags"
              path="tags"
              showWhen={{
                value: '{{ tags.length }}',
                notEquals: 0,
              }}
            />
          </CustomViewComponent>
        </CustomViewComponent>
      </>
    );
  },
});
CustomViewPages.register({
  id: 'builder_component',
  meta: {
    label: 'Component Detail Page',
    category: CustomViewPageCategory.BUILDER,
    typeId: {
      type: CustomViewTypeIdType.COMPONENT_TYPE,
    },
    templateId: {
      type: CustomViewTemplateIdType.COMPONENT_TYPE,
    },
    preview: {
      thumbnail: { src: componentThumbnail },
      items: [{ src: componentPreview }],
    },
  },
  function: function Entity() {
    return (
      <>
        <CustomViewComponent type="catalog_warnings" />
        <CustomViewComponent type="include" id="builder_component_general" />
        <CustomViewComponent type="catalog_components" />
      </>
    );
  },
});
